import React, { useState, useEffect, useCallback, useRef } from 'react';
import { health } from './api';
import { formatDateTime, relativeTime } from './format';
import './Portal.css';

const STATUS_COPY = {
  ok: { title: 'Everything looks healthy', sub: 'All checks passed on the last run.' },
  warn: { title: 'Running, with warnings', sub: 'The site is up but some items need your attention.' },
  down: { title: 'Action needed', sub: 'At least one check failed. Parts of the portal will not work.' },
};

export default function HealthPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const [latency, setLatency] = useState(null);

  // Avoids a "set state on an unmounted component" warning when the webmaster
  // switches tabs while a poll is in flight.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const startedAt = Date.now();
    try {
      const result = await health.status();
      if (!mounted.current) return;
      setData(result);
      setError(null);
      setLatency(Date.now() - startedAt);
      setLastChecked(new Date());
    } catch (err) {
      if (!mounted.current) return;
      setError(err.message);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [autoRefresh, load]);

  if (loading) {
    return <div className="pt-loading">Running health checks…</div>;
  }

  if (error && !data) {
    return <div className="pt-notice pt-notice-error">{error}</div>;
  }

  const copy = STATUS_COPY[data.status] || STATUS_COPY.warn;
  const failing = data.checks.filter((check) => check.status !== 'ok');

  return (
    <div>
      <div className={'pt-status-banner is-' + data.status}>
        <span className="pt-status-beacon" />
        <div className="pt-status-text">
          <h2>{copy.title}</h2>
          <p>
            {copy.sub}
            {failing.length > 0 && ' ' + failing.length + ' of ' + data.checks.length + ' checks need a look.'}
          </p>
        </div>
        <div className="pt-topbar-spacer" />
        <div className="pt-actions">
          <label
            className="pt-hint"
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 700 }}
          >
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button className="pt-btn pt-btn-ghost pt-btn-sm" onClick={load} type="button">
            Run checks now
          </button>
        </div>
      </div>

      {error && (
        <div className="pt-notice pt-notice-warn" style={{ marginBottom: 16 }}>
          Last refresh failed: {error} — showing the previous result.
        </div>
      )}

      <section className="pt-section">
        <div className="pt-section-head">
          <h2 className="pt-section-title">Checks</h2>
          <span className="pt-hint">
            {lastChecked ? 'Checked ' + relativeTime(lastChecked) : ''}
            {latency !== null ? ' · responded in ' + latency + ' ms' : ''}
          </span>
        </div>

        <div className="pt-checks">
          {data.checks.map((check) => (
            <div key={check.id} className={'pt-check is-' + check.status}>
              <div className="pt-check-top">
                <span className="pt-check-label">{check.label}</span>
                <span className={'pt-pill pt-pill-' + check.status}>
                  <span className="pt-dot" />
                  {check.status === 'ok' ? 'OK' : check.status === 'warn' ? 'Warn' : 'Fail'}
                </span>
              </div>
              <div className="pt-check-detail">{check.detail}</div>
              {check.hint && <div className="pt-check-hint">{check.hint}</div>}
            </div>
          ))}
        </div>
      </section>

      <section className="pt-section">
        <h2 className="pt-section-title">Server</h2>
        <div className="pt-card">
          <div className="pt-table-wrap">
            <table className="pt-table">
              <tbody>
                <tr>
                  <td style={{ fontWeight: 700, width: 180 }}>Host</td>
                  <td>{data.server.host}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 700 }}>Software</td>
                  <td>{data.server.software}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 700 }}>PHP</td>
                  <td>{data.server.php_version}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 700 }}>HTTPS</td>
                  <td>
                    <span className={'pt-pill pt-pill-' + (data.server.https ? 'ok' : 'down')}>
                      <span className="pt-dot" />
                      {data.server.https ? 'Secure' : 'Not secure'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 700 }}>Public URL</td>
                  <td>{data.server.app_url}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 700 }}>Checked at</td>
                  <td>{formatDateTime(data.checked_at)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {data.recent_errors.length > 0 && (
        <section className="pt-section">
          <h2 className="pt-section-title">Most recent errors</h2>
          <div className="pt-log">
            {data.recent_errors.map((entry, index) => (
              <div className="pt-log-row" key={index}>
                <span className="pt-log-time">{formatDateTime(entry.ts)}</span>
                <span className="pt-log-level error">error</span>
                <span className="pt-log-msg">{entry.message}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
